import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Adds an `is-visible` class to elements with class `tf-reveal` once they
 * enter the viewport. Used for subtle fade-in-up animations.
 *
 * Re-runs on every route change so newly mounted `.tf-reveal` elements on
 * SPA-navigated pages are also observed. Without this, only the very first
 * page load gets the reveal animation — subsequent pages stay invisible
 * because their `.tf-reveal` elements were never registered with the
 * IntersectionObserver.
 */
export default function useReveal() {
  const location = useLocation();

  useEffect(() => {
    // Wait one frame so React has committed the new route's DOM before we
    // query for `.tf-reveal` elements.
    const raf = requestAnimationFrame(() => {
      const els = document.querySelectorAll(".tf-reveal:not(.is-visible)");
      if (els.length === 0) return;
      if (!("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-visible"));
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
      );
      els.forEach((el) => observer.observe(el));
      // Safety net: if for any reason the observer never fires (e.g. element
      // already in viewport with 0 intersection due to layout shifts), make
      // sure cards become visible shortly after mount.
      const fallback = setTimeout(() => {
        els.forEach((el) => el.classList.add("is-visible"));
      }, 1200);
      // store cleanup on the observer so we can tear down in useEffect cleanup
      observer._fallback = fallback;
      observer._cleanup = () => {
        clearTimeout(fallback);
        observer.disconnect();
      };
      // attach to window so cleanup can run
      useReveal._observer?._cleanup?.();
      useReveal._observer = observer;
    });
    return () => {
      cancelAnimationFrame(raf);
      useReveal._observer?._cleanup?.();
      useReveal._observer = null;
    };
  }, [location.pathname]);
}
